// @ts-strict-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectOptionDefVO, ProjectOptionLocalizationVO, ProjectOptionVO } from '@project/model/vo/project.vo';
import { ProjectOptionUtils } from '../util/project-option.utils';
import { Dictionary } from 'typescript-collections';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { Subject } from 'rxjs';
import { StringUtils } from '@shared/util/string-utils';
import { CustomValidators } from '@shared/custom-validators';
import { AlertHelper } from '@shared/alert/alert.helper';
import { cloneDeep } from 'lodash';
import { ProjectOptionWrapper } from '@project/components/project/util/project-option-data';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { DateFormatter } from '@shared/date-formatter';
import { ProjectSettingsService } from './project-settings.service';
import { ProjectUserModel } from '../../../model/project-user.model';
import { ProjectGroupService } from '../../../service/project-group.service';
import { UserModel } from '@user/model/user.model';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { InputAutoFocusDirective } from '@shared/directives/input-auto-focus.directive';
import { BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import { CollapseDirective } from 'ngx-bootstrap/collapse';

// Modal

@Component({
  selector: 'app-project-settings',
  templateUrl: './project-settings.component.html',
  styleUrls: ['./project-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    InputAutoFocusDirective,
    BsDatepickerDirective,
    CollapseDirective
  ]
})
export class ProjectSettingsComponent implements OnInit {

  @Input() projectGroup: ProjectGroupVO;
  @Input() isArticleProjectGroup: boolean;

  @Input() saveEvent: Subject<any>;

  @Input() isCollapsed: boolean;

  /**
   * Means that neither project or any configuration of current project are not yet saved to the database
   */
  @Input() isTemporaryProjectMode: boolean;

  @Input() isCanceling: boolean = false;

  @Output() saved: EventEmitter<any> = new EventEmitter<any>();

  public restProjectOptions: ProjectOptionVO[] = [];
  public wrappedProjectOptions: ProjectOptionWrapper[] = [];

  public form: FormGroup;
  public items: FormArray;

  private parentGroup: ProjectGroupVO;

  public editEnabled: boolean = true;

  private showRequiredFields: boolean = false;

  public get dateValue(): Date {
    const dateFormControl = this.getFormControlByIndex(2);
    if (dateFormControl && dateFormControl.value && dateFormControl.value instanceof ProjectOptionVO && dateFormControl.value.value) {
      return DateFormatter.parseShortDate(dateFormControl.value.value);
    }
    return null;
  }

  public setDateValue(value: Date) {
    const dateFormControl = this.getFormControlByIndex(2);
    if (dateFormControl && dateFormControl.value && dateFormControl.value instanceof ProjectOptionVO) {
      dateFormControl.value.value = value ? DateFormatter.formatShortDate(value) : '';
      dateFormControl.setValue(dateFormControl.value);
    }
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly cdRef: ChangeDetectorRef,
    private readonly localeResourceManager: LocaleResourceManager,
    private readonly alertHelper: AlertHelper,
    private readonly projectOptionUtils: ProjectOptionUtils,
    private readonly projectSettingsService: ProjectSettingsService,
    private readonly projectUserModel: ProjectUserModel,
    private readonly projectGroupService: ProjectGroupService,
    private readonly userModel: UserModel,
  ) {}

  public ngOnInit(): void {
    if (this.saveEvent) {
      this.saveEvent.subscribe(() => {
        if (!this.isTemporaryProjectMode && !this.isNewGroup()) {
          this.loadParentProjectGroup().then(() => this.saveProjectGroupOptions());
        } else {
          this.saveProjectGroupOptions();
        }
      });
    }
    this.form = this.formBuilder.group({
      projectName: [
        this.projectGroup?.groupName ?? "",
        Validators.compose([CustomValidators.projectNameValidator(false), CustomValidators.maxLength(ProjectGroupUtils.MAX_GROUP_NAME_LENGTH)]),
      ],
      items: this.formBuilder.array([]),
    });
    if (!this.isTemporaryProjectMode && !this.isNewGroup()) {
      this.loadParentProjectGroup();
      this.editEnabled =
        (this.projectGroup?.groupOwner.userId === this.userModel.user.id || this.projectGroup.isSharedReadOnly !== true) &&
        !this.userModel.user.isProduction();
    }
    this.mergeDefaultAndProjectGroupOptions();
    this.createFormData();

    if (!this.editEnabled) {
      this.form.disable();
    }
  }

  public updateOptionValue(event, formControl: AbstractControl): void {
    if (event && event.target) {
      formControl.value.value = event.target.value;
      formControl.setValue(formControl.value);
    }
  }

  public getProjectNameFormControl() {
    return this.form.get('projectName') as FormControl;
  }

  public getFormControlByIndex(index: number) {
    const formArray: FormArray = this.form.get('items') as FormArray;
    const formGroup: FormGroup = formArray ? (formArray.controls[index] as FormGroup) : null;
    return formGroup && formGroup.controls.formControl ? formGroup.controls.formControl : null;
  }

  public markDirty(control: AbstractControl): void {
    if (control) {
      setTimeout(() => {
        if (!this.isCanceling) {
          control.markAsDirty();
        }
        this.cdRef.markForCheck();
      }, 100);
    }
  }

  public checkControlValidity(control: AbstractControl): boolean {
    if (!control) {
      return;
    }
    return (control.invalid && control.dirty) || (control.invalid && this.showRequiredFields);
  }

  public getLocalizedProjectOptionLabel(projectOptionLocalizations: ProjectOptionLocalizationVO[]): string {
    if (ArrayUtils.isEmpty(projectOptionLocalizations)) {
      return null;
    }
    const localeChain: string[] = this.localeResourceManager.getLocaleChain();
    if (ArrayUtils.isEmpty(localeChain)) {
      return this.getLocalizedProjectOptionLocalizationValue(projectOptionLocalizations, null);
    } else {
      for (const locale of localeChain) {
        const value: string = this.getLocalizedProjectOptionLocalizationValue(projectOptionLocalizations, locale);
        if (!StringUtils.isEmpty(value)) {
          return value;
        }
      }
      return null;
    }
  }

  private mergeDefaultAndProjectGroupOptions(): ProjectOptionVO[] {
    const mergedOptions: any[] = [];
    // save converted options
    const projectGroupOptions: ProjectOptionVO[] = this.copyProjectOptionArray(this.projectGroup.options);
    const optionDefs: ProjectOptionDefVO[] = this.projectUserModel.projectGroupOptionDefs;
    optionDefs.sort((o1, o2) => NumberUtils.numericCompare(o1.order, o2.order));
    for (const optionDef of optionDefs) {
      // invisbible items are now merged as well and visibility is handled later
      if (!optionDef.isVisible) {
        continue;
      }
      const convertedOption: ProjectOptionVO = ProjectOptionUtils.convertProjectOptionDefToProjectOption(optionDef);
      const projectOption: ProjectOptionVO = ProjectOptionUtils.findProjectOptionByIdentifier(projectGroupOptions, convertedOption.identifier);
      if (projectOption) {
        convertedOption.id = projectOption.id;
        convertedOption.value = projectOption.value;
        convertedOption.localizations = this.getMergedProjectOptionLocalizations(convertedOption.localizations, projectOption.localizations);
        // delete repeat element
        projectGroupOptions.splice(projectGroupOptions.indexOf(projectOption), 1);
      }
      mergedOptions.push(convertedOption);
      // created wrapper for project option to store "required" value
      const wrappedProjectOption: ProjectOptionWrapper = this.projectOptionUtils.createWrapperByProjectOption(
        convertedOption,
        optionDef.isRequired,
        optionDef.isMultiLine
      );
      this.wrappedProjectOptions.push(wrappedProjectOption);
    }
    this.restProjectOptions = projectGroupOptions;
    return mergedOptions;
  }

  private getMergedProjectOptionLocalizations(
    userOptionLocalizations: ProjectOptionLocalizationVO[],
    projectOptionLocalizations: ProjectOptionLocalizationVO[]
  ): ProjectOptionLocalizationVO[] {
    if (ArrayUtils.isEmpty(projectOptionLocalizations)) {
      return userOptionLocalizations;
    }
    if (ArrayUtils.isEmpty(userOptionLocalizations)) {
      return projectOptionLocalizations;
    }
    const result: ProjectOptionLocalizationVO[] = projectOptionLocalizations.concat();
    const includedLocales: Dictionary<string, boolean> = new Dictionary<string, boolean>();
    for (const projectOptionLocalization of projectOptionLocalizations) {
      if (projectOptionLocalization) {
        includedLocales.setValue(projectOptionLocalization.locale, true);
      }
    }
    for (const userOptionLocalization of userOptionLocalizations) {
      if (userOptionLocalization && !includedLocales.containsKey(userOptionLocalization.locale)) {
        result.push(userOptionLocalization);
      }
    }
    return result;
  }

  private copyProjectOptionArray(projectOptions: ProjectOptionVO[]): ProjectOptionVO[] {
    const newArray: ProjectOptionVO[] = [];
    if (ArrayUtils.isEmpty(projectOptions)) {
      return newArray;
    }
    for (const projectOption of projectOptions) {
      newArray.push(Object.assign(new ProjectOptionVO(), projectOption));
    }
    return newArray;
  }

  private createFormData(): void {
    this.items = this.form.get('items') as FormArray;

    const maxValueLengthValidator = (formGroup: FormGroup) => {
      const control: any = formGroup.controls.formControl;
      if (control && control.value instanceof ProjectOptionVO && control.value.value) {
        if (control.value.value.length > ProjectOptionUtils.MAX_PROJECT_OPTION_VALUE_LENGTH) {
          control.setErrors({
            messageKey: 'validation.error.too_many_chars',
            param: ProjectOptionUtils.MAX_PROJECT_OPTION_VALUE_LENGTH,
          });
        } else {
          control.setErrors(null);
        }
        return null;
      }
    };

    const validator = (formGroup: FormGroup) => {
      const control: any = formGroup.controls.formControl;
      if (control && control.value instanceof ProjectOptionVO && (!control.value.value || !control.value.value.trim())) {
        control.setErrors({
          messageKey: `validation.error.required_field`,
        });
      } else {
        maxValueLengthValidator(formGroup);
      }
    };

    for (const optionWrapper of this.wrappedProjectOptions) {
      const formItem = this.formBuilder.group({});
      if (optionWrapper.visible) {
        const projectOption: ProjectOptionVO = optionWrapper.projectOption;
        formItem.validator = optionWrapper.required ? validator : maxValueLengthValidator;
        formItem.addControl('formControl', new FormControl(projectOption));
      }
      this.items.push(formItem);
    }
  }

  private getLocalizedProjectOptionLocalizationValue(projectOptionLocalizations: ProjectOptionLocalizationVO[], locale: string): string {
    if (ArrayUtils.isEmpty(projectOptionLocalizations)) {
      return null;
    }
    let unlocalizedValue: string = null;
    for (const projectOptionLocalization of projectOptionLocalizations) {
      if (projectOptionLocalization.locale === locale) {
        return projectOptionLocalization.value;
      } else if (!projectOptionLocalization.locale) {
        unlocalizedValue = projectOptionLocalization.value;
      }
    }
    return unlocalizedValue;
  }

  private async saveProjectGroupOptions(): Promise<void> {
    if (this.form.invalid) {
      this.showRequiredFields = true;
      // expand dialog if any of collapsed input is invalid
      const formArray: FormArray = this.form.get('items') as FormArray;
      for (let i = 1; i < formArray.controls.length; i++) {
        const formControl = this.getFormControlByIndex(i);
        if (formControl && this.checkControlValidity(formControl as FormControl)) {
          this.isCollapsed = false;
          break;
        }
      }
      this.cdRef.markForCheck();
      return;
    }
    const projectOptions = this.wrappedProjectOptions.map(wrappedOption => wrappedOption.projectOption);
    const targetProjectGroup: ProjectGroupVO = this.isTemporaryProjectMode ? this.projectGroup : cloneDeep(this.projectGroup);
    targetProjectGroup.groupName = this.form.controls.projectName.value;
    targetProjectGroup.groupType = this.projectGroup.groupType;

    if (StringUtils.isEmpty(targetProjectGroup.groupName)) {
      targetProjectGroup.groupName = this.localeResourceManager.getValueByKey('header.menu.new_project');
    }

    targetProjectGroup.options = projectOptions.concat(this.restProjectOptions);
    if (this.isTemporaryProjectMode) {
      return;
    }
    if (this.isNewGroup()) {
      this.projectGroup.groupName = targetProjectGroup.groupName;
      this.projectGroup.options = targetProjectGroup.options;
      this.projectSettingsService.notifyProjectGroupUpdatedSuccess(this.projectGroup, !!this.isArticleProjectGroup);
    } else {
      await this.saveProjectGroup(targetProjectGroup);
    }
  }

  private async saveProjectGroup(projectGroup: ProjectGroupVO): Promise<void> {
    try {
      projectGroup = await this.projectGroupService.saveProjectGroup(this.isArticleProjectGroup, projectGroup);
      this.projectGroup.id = projectGroup.id;
      this.projectGroup.groupName = projectGroup.groupName;
      this.projectGroup.options = projectGroup.options;
      this.saved.emit();
      this.projectSettingsService.notifyProjectGroupUpdatedSuccess(projectGroup, !!this.isArticleProjectGroup);
    } catch (error) {
      this.projectSettingsService.notifyProjectGroupUpdatedFailure();
      const message: string = error.statusText ? error.statusText : '';
      await this.alertHelper.showError('project_settings.title.error_saving', 'project_settings.message.error_saving', {translationParams: [message]});
    }
  }

  private async loadParentProjectGroup(): Promise<void> {
    if (this.projectGroup.parentGroup) {
      const parentProjectGroup = await this.projectGroupService.loadProjectGroupById(this.isArticleProjectGroup, this.projectGroup.parentGroup.id);
      this.parentGroup = parentProjectGroup;
      this.updateValidators();
    } else {
      const baseGroups = await this.projectGroupService.loadBaseProjectGroups(this.isArticleProjectGroup);
      this.parentGroup = new ProjectGroupVO();
      this.parentGroup.groups = baseGroups;
      this.updateValidators();
    }
  }

  private updateValidators(): void {
    const groupNameExistValidator = (formControl: FormControl) => {
      if (ArrayUtils.isNotEmpty(this.parentGroup.groups)) {
        for (const pg of this.parentGroup.groups) {
          if (pg.groupName === this.form.controls.projectName.value && pg.id !== this.projectGroup.id) {
            return {
              messageKey: `editProject.message.project_name_use_in_folder`,
            };
          }
        }
      }
    };
    this.form.controls.projectName.setValidators(
      Validators.compose([
        CustomValidators.projectNameValidator(false),
        CustomValidators.maxLength(ProjectGroupUtils.MAX_GROUP_NAME_LENGTH),
        groupNameExistValidator,
      ])
    );
    this.form.controls.projectName.updateValueAndValidity();
  }

  private isNewGroup(): boolean {
    return NumberUtils.isNotNumber(this.projectGroup.id) || this.projectGroup.id <= 0;
  }
}
